import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { ToastContainer, toast, Slide } from 'react-toastify';
import { analyticsTrack } from '../../analyticsUtils';
import EmployerInterviewCard from './EmployerInterviewCard';
import './EmployerInterviews.scss';

const CARDS_PER_BLOCK = 10;

const emptyInterviewsCopy = {
  upcoming: 'When you schedule an interview with a candidate, you will find the interview details here.',
  pending: 'When you are waiting for candidates to confirm interviews, you will find the invitation details here.',
  past: 'When you have held an interview with a candidate, you will find the details of that interview here.',
  reschedule: 'When there is a reschedule request by a candidate, you will find the details of that interview here.',
};

const renderTab = (active, onClick, copy, count) => (
  <a
    role="button"
    tabIndex="0"
    onClick={onClick}
    className={`body-text-2 medium-weight tab-active-${active} ${copy}-tab`}
  >
    {copy}
    {' '}
    (
    {count}
    )
  </a>
);

const allowedTypes = ['upcoming', 'pending', 'reschedule', 'past'];

class EmployerInterviews extends React.Component {
  constructor(props) {
    super(props);

    const defaultTypeProp = allowedTypes.includes(this.props.type) ? this.props.type : 'upcoming';

    this.state = {
      type: defaultTypeProp,
      numCardsDisplayed: {
        upcoming: CARDS_PER_BLOCK,
        past: CARDS_PER_BLOCK,
        pending: CARDS_PER_BLOCK,
        reschedule: CARDS_PER_BLOCK,
      },
    };

    this.clickTab = this.clickTab.bind(this);
  }

  componentDidMount() {
    analyticsTrack('Viewed My Interviews Page', {});
    const { toastMessage, rescheduleLink } = this.props;

    if (toastMessage) {
      toast.success(
        <span>
          {toastMessage}
          &nbsp;Reschedule
          {' '}
          <a className="reschedule-interview-link" href={rescheduleLink}>here.</a>
        </span>,
      );
    }
  }

  getInterviewsToRender() {
    const { type, numCardsDisplayed } = this.state;
    const { interviews } = this.props;

    return interviews[type].slice(0, numCardsDisplayed[type]);
  }

  canShowMore() {
    const { type, numCardsDisplayed } = this.state;
    const interviewsByType = this.props.interviews[type];

    return numCardsDisplayed[type] < interviewsByType.length;
  }

  clickTab(type) {
    this.setState({ type });
  }

  clickShowMore() {
    const { type, numCardsDisplayed } = this.state;

    const oldNumOfType = numCardsDisplayed[type];
    const newNumCardsDisplayed = update(
      numCardsDisplayed,
      {
        [type]: { $set: oldNumOfType + CARDS_PER_BLOCK },
      },
    );

    this.setState({ numCardsDisplayed: newNumCardsDisplayed });
  }

  renderEmptyInterviews() {
    const { type } = this.state;

    return (
      <div className="body-text-2 normal-weight">
        {emptyInterviewsCopy[type]}
      </div>
    );
  }

  render() {
    const { type } = this.state;
    const { interviews } = this.props;

    const interviewsToRender = this.getInterviewsToRender();
    return (
      <div className="employer-interviews-container style-guide">
        <ToastContainer
          position="bottom-center"
          transition={Slide}
          hideProgressBar
          autoClose={8000}
          closeOnClick
          draggable={false}
          pauseOnHover
          pauseOnFocusLoss
          className="cancelled-interview-toast-container"
          toastClassName="cancelled-interview-toast"
          bodyClassName="cancelled-interview-toast-body"
        />
        <div className="dashboard-header-container">
          <div className="dashboard-header">
            <h1 className="mb-3">My Interviews</h1>
            <div className="interview-tabs g-row-always">
              {renderTab(type === 'upcoming', () => this.clickTab('upcoming'), 'Upcoming', interviews.upcoming.length)}
              {renderTab(type === 'pending', () => this.clickTab('pending'), 'Pending', interviews.pending.length)}
              {renderTab(type === 'reschedule', () => this.clickTab('reschedule'), 'Reschedule Request', interviews.reschedule.length)}
              {renderTab(type === 'past', () => this.clickTab('past'), 'Past', interviews.past.length)}
            </div>
          </div>
        </div>

        <div className="employer-interviews-body">
          {interviewsToRender.length > 0
            ? interviewsToRender.map((interview) => (
              <EmployerInterviewCard
                key={interview.id}
                interview={interview}
              />
            ))
            : this.renderEmptyInterviews()}
        </div>

        {
          this.canShowMore()
            && (
            <div className="show-more-interviews-container width-790-centered">
              <a
                role="button"
                tabIndex="0"
                className="show-more-interviews btn btn-secondary"
                onClick={() => this.clickShowMore()}
              >
                Show More
              </a>
            </div>
            )
        }
      </div>
    );
  }
}

EmployerInterviews.propTypes = {
  interviews: PropTypes.shape({
    upcoming: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    pending: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    past: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  type: PropTypes.oneOf(allowedTypes),
  toastMessage: PropTypes.string,
  rescheduleLink: PropTypes.string,
};

EmployerInterviews.defaultProps = {
  type: 'upcoming',
  toastMessage: null,
  rescheduleLink: '#',
};

export default EmployerInterviews;
